import React, { useState, useRef, useEffect } from "react";
import {
  Menu,
  ShoppingBag,
  Heart,
  Home,
  Search,
  Grid,
  Award,
  User,
  ChevronDown,
  Palette,
  Globe,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { useSearchAPI } from "./useSearchAPI";
import SearchBar from "./SearchBar";
import ThemeToggle from "../ThemeToggle";
import FullScreenSearchOverlay from "./search-suggestions/FullScreenSearchOverlay";

interface Props {
  onCartClick: () => void;
  onMenuClick: () => void;
  onProductClick?: (p: any) => void;
  isScrolling?: boolean;
}

export default function SingleHeader({
  onCartClick,
  onMenuClick,
  onProductClick,
  isScrolling,
}: Props) {
  const {
    cart,
    lang,
    setLang,
    activeTab,
    setActiveTab,
    categories,
    setSearchQuery,
    showThemePalette,
    setShowThemePalette,
  } = useApp();
  const api = useSearchAPI(onProductClick);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const popularCategoriesFallback = [
    {
      id: "c1",
      nameEn: "Electronics",
      nameBn: "গ্যাজেট ও ডিজিটাল আইটেম",
      image:
        "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c2",
      nameEn: "Skincare",
      nameBn: "স্কিনকেয়ার",
      image:
        "https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c3",
      nameEn: "Haircare",
      nameBn: "হেয়ারকেয়ার",
      image:
        "https://images.unsplash.com/photo-1522337660859-02fbefca4702?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c4",
      nameEn: "Fragrance",
      nameBn: "সুগন্ধি",
      image:
        "https://images.unsplash.com/photo-1594035910387-fea477274976?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c5",
      nameEn: "Bath & Body",
      nameBn: "গোসল",
      image:
        "https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c6",
      nameEn: "Tools",
      nameBn: "টুলস",
      image:
        "https://images.unsplash.com/photo-1571781926291-c477ebef0124?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c7",
      nameEn: "Lips",
      nameBn: "ঠোঁট",
      image:
        "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c8",
      nameEn: "Men's Grooming",
      nameBn: "ছেলেদের গ্রুমিং",
      image:
        "https://images.unsplash.com/photo-1626015509747-d1a2eb3e45dd?w=300&auto=format&fit=crop&q=80",
    },
    {
      id: "c9",
      nameEn: "Combo Packs",
      nameBn: "কম্বো প্যাক",
      image:
        "https://images.unsplash.com/photo-1608248593822-8eb3a6cebebd?w=300&auto=format&fit=crop&q=80",
    },
  ];

  const displayCategories =
    categories && categories.length > 0
      ? categories
      : popularCategoriesFallback;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCategoryDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navs = [
    { key: "home", en: "Home", bn: "হোম", Icon: Home },
    { key: "deals", en: "Deals", bn: "অফার", Icon: Award },
    { key: "orders", en: "Account", bn: "অ্যাকাউন্ট", Icon: User },
  ];

  return (
    <header
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      className={`fixed top-0 left-0 right-0 z-[100] w-full border-b border-transparent bg-transparent lg:bg-white/90 lg:dark:bg-[#0A0A0C]/90 lg:backdrop-blur-xl lg:border-neutral-200/50 lg:dark:border-white/10 px-3 sm:px-4 lg:px-8 flex items-center transition-transform duration-350 ease-in-out ${isScrolling ? "-translate-y-full lg:translate-y-0 pointer-events-none lg:pointer-events-auto" : "translate-y-0"}`}
    >
      <div className="absolute inset-0 bg-white/80 dark:bg-[#0A0A0C]/80 backdrop-blur-xl lg:hidden border-none -z-10 shadow-sm" />

      {/* Main Header Content Wrapper */}
      <div className="w-full flex items-center justify-between gap-3 sm:gap-6 relative z-10 py-4 sm:py-5 lg:py-6">
        {/* Left Side (Menu + Logo) */}
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 lg:w-1/4 shrink-0 min-w-0">
        <button
          onClick={onMenuClick}
          className="p-2 sm:p-2.5 rounded-full hover:bg-neutral-100/50 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-200 transition-colors cursor-pointer shrink-0"
        >
          <Menu className="w-6 h-6 stroke-[2.2px]" />
        </button>
        <div 
          onClick={() => setActiveTab("home")}
          className="flex items-center gap-2 cursor-pointer group select-none transition-transform hover:scale-105 active:scale-95"
        >
          <div className="bg-primary w-9 h-9 sm:w-10 sm:h-10 rounded-full shadow-md group-hover:shadow-lg transition-all flex items-center justify-center shrink-0 animate-logo">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7">
              <path fillRule="evenodd" clipRule="evenodd" d="M8 8V6a4 4 0 1 1 8 0v2h2.5A1.5 1.5 0 0 1 20 9.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 20.5v-11A1.5 1.5 0 0 1 5.5 8H8Zm2 0h4V6a2 2 0 1 0-4 0v2Zm2 10.5l-.6-.55c-2.1-1.9-3.6-3.4-3.6-5 0-1.3 1-2.3 2.3-2.3.7 0 1.4.3 1.9.8.5-.5 1.2-.8 1.9-.8 1.3 0 2.3 1 2.3 2.3 0 1.6-1.5 3.1-3.6 5l-.6.55z" fill="currentColor"/>
            </svg>
          </div>
          <span className="text-xl sm:text-2xl lg:text-3xl truncate font-extrabold tracking-tight text-neutral-900 dark:text-white flex items-center drop-shadow-[0_0_12px_rgba(255,255,255,0.1)] animate-fade-in">
            Priyofy
            <span className="text-rose-500 text-2xl sm:text-3xl leading-[0] ml-0.5">.</span>
          </span>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden lg:flex items-center justify-center gap-1 xl:gap-2 flex-1">
        {/* Home Tab */}
        <button
          onClick={() => {
            setActiveTab("home");
            setShowCategoryDropdown(false);
          }}
          className={`flex items-center gap-1.5 px-3 xl:px-4 py-2 rounded-full font-black text-sm transition-all duration-300 ${
            activeTab === "home" && !showCategoryDropdown
              ? "bg-primary/15 text-primary backdrop-blur-md border border-primary/10 shadow-sm"
              : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/60 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white hover:backdrop-blur-sm"
          }`}
        >
          <Home className="w-4 h-4 stroke-[2.5px]" />
          <span>{lang === "en" ? "Home" : "হোম"}</span>
        </button>

        {/* Categories Dropdown Tab */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowCategoryDropdown(prev => !prev)}
            onMouseEnter={() => setShowCategoryDropdown(true)}
            className={`flex items-center gap-1.5 px-3 xl:px-4 py-2 rounded-full font-black text-sm transition-all duration-300 ${
              showCategoryDropdown || activeTab === "categories"
                ? "bg-primary/15 text-primary backdrop-blur-md border border-primary/10 shadow-sm"
                : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/60 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white hover:backdrop-blur-sm"
            }`}
          >
            <Grid className="w-4 h-4 stroke-[2.5px]" />
            <span>{lang === "en" ? "Categories" : "ক্যাটাগরি"}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-300 ${showCategoryDropdown ? "rotate-180" : ""}`}
            />
          </button>

          {/* Categories Dropdown Panel */}
          {showCategoryDropdown && (
            <div
              className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 sm:w-[320px] bg-white dark:bg-[#121215] border border-neutral-200/80 dark:border-white/10 rounded-2xl shadow-xl py-3 px-2 z-[200] animate-slide-up flex flex-col gap-1 backdrop-blur-xl"
              onMouseLeave={() => setShowCategoryDropdown(false)}
            >
              <div className="px-3 py-1 text-[11px] font-black uppercase text-neutral-450 tracking-wider border-b border-neutral-100 dark:border-white/5 pb-2 mb-1.5">
                {lang === "en"
                  ? "Shop by Categories"
                  : "ক্যাটাগরি অনুযায়ী শপিং"}
              </div>
              <div className="grid grid-cols-1 gap-1 max-h-[350px] overflow-y-auto scrollbar-none">
                {displayCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSearchQuery(cat.nameEn);
                      setActiveTab("search");
                      setShowCategoryDropdown(false);
                    }}
                    className="flex items-center gap-3 w-full p-2 rounded-xl text-left hover:bg-neutral-50 dark:hover:bg-white/5 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden border border-neutral-100 dark:border-white/5 group-hover:border-primary/30 shrink-0">
                      <img
                        src={cat.image}
                        className="w-full h-full object-cover"
                        alt={cat.nameEn}
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="text-xs font-bold text-neutral-800 dark:text-neutral-200 group-hover:text-primary transition-colors truncate">
                        {lang === "en" ? cat.nameEn : cat.nameBn}
                      </div>
                      <div className="text-[10px] text-neutral-450 font-medium">
                        {lang === "en" ? "Explore items" : "প্রোডাক্টস দেখুন"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Other Tabs */}
        {navs.slice(1).map((nav) => {
          const isActive = activeTab === nav.key;
          return (
            <button
              key={nav.key}
              onClick={() => {
                setActiveTab(nav.key as any);
                setShowCategoryDropdown(false);
              }}
              className={`flex items-center gap-1.5 px-3 xl:px-4 py-2 rounded-full font-black text-sm transition-all duration-300 ${
                isActive && !showCategoryDropdown
                  ? "bg-primary/15 text-primary backdrop-blur-md border border-primary/10 shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100/60 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white hover:backdrop-blur-sm"
              }`}
            >
              <nav.Icon className="w-4 h-4 stroke-[2.5px]" />
              <span>{lang === "en" ? nav.en : nav.bn}</span>
            </button>
          );
        })}
      </nav>

      {/* Right Side (Desktop Search + Icons) */}
      <div className="flex items-center justify-end gap-1 sm:gap-3 shrink-0 lg:w-1/4">
        {/* Desktop Search */}
        <div className="hidden lg:block w-56 xl:w-72">
          <SearchBar api={api} compact={true} />
        </div>

        {/* Mobile Search Icon */}
        <button
          onClick={() => api.setShowSuggestions(!api.showSuggestions)}
          className="lg:hidden p-2 rounded-full hover:bg-neutral-100/50 dark:hover:bg-white/10 text-neutral-600 dark:text-neutral-300 transition-colors cursor-pointer shrink-0"
        >
          <Search className="w-5 h-5 sm:w-5.5 sm:h-5.5 stroke-[2px]" />
        </button>

        {/* Quick Toggles */}
        <button
          onClick={() => setShowThemePalette(prev => !prev)}
          className="p-2.5 rounded-full bg-neutral-100/80 dark:bg-white/10 hover:text-primary hover:bg-rose-50 dark:hover:bg-rose-500/20 transition-all cursor-pointer shadow-sm active:scale-95 text-neutral-600 dark:text-neutral-300 hidden sm:flex"
          aria-label="Theme Colors"
        >
          <Palette className="w-5 h-5 sm:w-5.5 sm:h-5.5 stroke-[2px]" />
        </button>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setLang(lang === "bn" ? "en" : "bn")}
            className="p-2 sm:p-2.5 rounded-full bg-neutral-100/80 dark:bg-white/10 hover:bg-neutral-200/80 dark:hover:bg-white/20 text-neutral-700 dark:text-neutral-200 transition-all cursor-pointer shadow-sm active:scale-95 flex items-center justify-center gap-1 font-bold text-xs"
            title={lang === "bn" ? "Switch to English" : "বাংলায় পরিবর্তন করুন"}
            aria-label="Toggle Language"
          >
            <Globe className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-primary stroke-[2px]" />
            <span className="font-extrabold text-[11px] sm:text-xs text-neutral-800 dark:text-neutral-200">
              {lang === "bn" ? "EN" : "BN"}
            </span>
          </button>
          <ThemeToggle />
        </div>

        <button
          onClick={onCartClick}
          className="relative p-2.5 rounded-full bg-neutral-100/80 dark:bg-white/10 hover:text-primary hover:bg-rose-50 dark:hover:bg-rose-500/20 transition-all cursor-pointer shadow-sm active:scale-95"
        >
          <ShoppingBag className="w-4.5 h-4.5 sm:w-5 sm:h-5 stroke-[2px]" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-primary text-[9px] font-black text-white border border-white dark:border-[#0A0A0C]">
              {cartCount}
            </span>
          )}
        </button>
      </div>
      </div>

      <FullScreenSearchOverlay api={api} />
    </header>
  );
}
